/**
 * Premium "Logo Circle Sweep" intro — PERFORMANCE-OPTIMIZED.
 *
 * Visuell: Ein dünner Marken-Ring zeichnet sich kreisförmig um das Logo
 * herum (Stroke-Sweep), das Logo poppt mittendrein, Headline steigt auf.
 *
 * Warum SVG statt `conic-gradient`?
 *   Die alte Version nutzte einen rotierenden `conic-gradient` + ein
 *   `filter: drop-shadow()` und einen riesigen `box-shadow` (120 px Blur).
 *   Conic-Gradients lassen sich nicht GPU-komponieren — jeder Frame muss
 *   neu auf CPU gerastert werden. Kombiniert mit grossen Shadow-Blur-
 *   Radien führte das zu sichtbarem Stottern, das sich auch nach Loops
 *   nicht erholte (akkumulierte Layer-Compositing-Kosten).
 *
 *   SVG-Strokes mit `stroke-dasharray` / `stroke-dashoffset` werden vom
 *   Browser dagegen als zusammengesetzte Vektor-Layer behandelt und
 *   laufen flüssig — auch auf schwächerer GPU. Plus: kein Filter und
 *   keine grossen Blur-Radien.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const logoCircleSweep: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const ringSize = isVertical ? 540 : 440;
  const logoH = isVertical ? 200 : 160;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const ringId = `${clipPrefix}-ring`;
  const arcId = `${clipPrefix}-arc`;
  const baseRingId = `${clipPrefix}-base`;
  const glowId = `${clipPrefix}-glow`;
  const logoId = `${clipPrefix}-logo`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const tickId = `${clipPrefix}-tick`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const logoMarkup = brandLogoImg({ id: `${clipPrefix}-img`, sizePx: logoH, color: themeColors.fg });

  // SVG-Setup: viewBox 100×100, Kreis r=46 → Umfang ≈ 2π·46 ≈ 289. Wir
  // nutzen dashLen 289 und animieren dashoffset von 289 → 0.
  const r = 46;
  const circumference = 2 * Math.PI * r;
  const tickMarks = Array.from({ length: 12 })
    .map((_, i) => {
      const angle = (i / 12) * 360 - 90;
      const rad = (angle * Math.PI) / 180;
      const x1 = 50 + Math.cos(rad) * 41;
      const y1 = 50 + Math.sin(rad) * 41;
      const x2 = 50 + Math.cos(rad) * 44;
      const y2 = 50 + Math.sin(rad) * 44;
      return `<line x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(3)}" stroke="${withAlpha(themeColors.fg, 0.22)}" stroke-width="0.6" stroke-linecap="round"/>`;
    })
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:48px;padding:96px;">
    <div id="${ringId}" style="position:relative;width:${ringSize}px;height:${ringSize}px;display:flex;align-items:center;justify-content:center;">
      <!-- Subtiler radialer Glow als statisches Hintergrund-Element. -->
      <div id="${glowId}" style="position:absolute;inset:-40px;border-radius:9999px;pointer-events:none;background:radial-gradient(circle at 50% 50%, ${withAlpha(accent, 0.35)} 0%, ${withAlpha(accent, 0.0)} 60%);"></div>

      <!-- SVG-Ring: GPU-freundlicher Stroke-Sweep statt rotierender Conic-Gradient. -->
      <svg viewBox="0 0 100 100" width="${ringSize}" height="${ringSize}" style="position:absolute;inset:0;overflow:visible;">
        <!-- Dünner Basis-Ring (immer sichtbar). -->
        <circle id="${baseRingId}" cx="50" cy="50" r="${r}" fill="none" stroke="${withAlpha(themeColors.fg, 0.12)}" stroke-width="0.6"/>
        <!-- Animierter Akzent-Bogen — wird via dashoffset eingezeichnet. -->
        <circle id="${arcId}" cx="50" cy="50" r="${r}" fill="none" stroke="${accent}" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="${circumference.toFixed(3)}" stroke-dashoffset="${circumference.toFixed(3)}" transform="rotate(-90 50 50)" style="filter:drop-shadow(0 0 4px ${withAlpha(accent, 0.85)});"/>
        <!-- Tick marks innerhalb der SVG (kein extra DOM-Layer). -->
        <g id="${tickId}" opacity="0">${tickMarks}</g>
      </svg>

      <!-- Logo zentral. -->
      <div id="${logoId}" style="position:relative;width:${ringSize - 80}px;height:${ringSize - 80}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;overflow:hidden;will-change:transform;">
        ${logoMarkup}
      </div>
    </div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1400}px;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${arcId}", { attr: { "stroke-dashoffset": ${circumference.toFixed(3)} } });`,
    `gsap.set("#${glowId}", { opacity: 0 });`,
    `gsap.set("#${tickId}", { opacity: 0 });`,
    `gsap.set("#${logoId}", { opacity: 0, scale: 0.5, transformOrigin: "50% 50%", force3D: true });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ].join("\n");

  const tweens: string[] = [
    // Arc zeichnet sich rundherum — `stroke-dashoffset` ist eine billige
    // Vektor-Animation, die der Browser auf einer eigenen Compositor-
    // Layer rastert.
    t.to(`#${arcId}`, { attr: { "stroke-dashoffset": 0 }, duration: 1.05, ease: "raw:\"power2.inOut\"" }, 0.05),
    // Statischer Glow blendet ein, sobald Arc gestartet ist.
    t.to(`#${glowId}`, { opacity: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.4),
    // Tick marks (innerhalb der SVG) blenden ein.
    t.to(`#${tickId}`, { opacity: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.55),
    // Logo poppt mittendrein.
    t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"", force3D: true }, 0.55),
    // Headline + sub.
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.05),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.25));

  // Dwell: KEINE kontinuierliche Animation auf dem Ring/Sweep — der Ring
  // bleibt einfach stehen. Optional pulsiert der Glow ganz sanft (billig).
  const dwell = Math.max(0.6, clipDurationSec - 1.5);
  tweens.push(
    t.to(`#${glowId}`, {
      opacity: 0.65,
      duration: dwell,
      ease: "raw:\"sine.inOut\"",
      yoyo: true,
      repeat: 1,
    }, 1.5),
  );

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
